/**
 * Состояние предпросмотра промпта на `/generate` — что показать в блоке «Промпт».
 *
 * `ready` — текст есть (в том числе прошлый, пока идёт пересборка); `error` — сборка упала, текста нет;
 * `loading` — пара есть и ждём ответ, или сцены/платья из адреса ещё грузятся; `none` — собирать не из чего.
 */
export type PromptView = "none" | "loading" | "error" | "ready";

export function promptView(s: { hasPair: boolean; inputsLoading: boolean; hasText: boolean; failed: boolean }): PromptView {
  if (s.hasText) return "ready";
  if (s.failed) return "error";
  if (s.hasPair || s.inputsLoading) return "loading";
  return "none";
}

/** Разбор сцены первой пары: `ready` — есть, `missing` — ещё нет (допишется при запуске), `null` — у пары его не бывает. */
export type SceneState = "ready" | "missing" | null;

/** Заготовка «Моего»: разбор сцены входит в неё только когда сцена одна — иначе он был бы чужим у остальных пар. */
export function takeBaseOf(r: { base: string; scene_base: string }, scenes: number): string {
  return scenes === 1 ? r.scene_base : r.base;
}

/** Подпись под текстом «Авто»: с разбором сцены текст — тот, что уйдёт в модель; без него он допишется при запуске. */
export function previewCaption(scene: SceneState, scenes: number): string {
  if (scene === null) return "Предпросмотр первой пары, собран из галочек правил — только чтение. Свой текст — «Мой».";
  const head =
    scene === "missing"
      ? "Предпросмотр первой пары, разбор сцены ещё не добавлен. Свой текст — «Мой»."
      : "Предпросмотр первой пары: этот текст уйдёт в модель. Свой текст — «Мой».";
  return scenes > 1 ? head + " У остальных сцен своя строка кадра и свои замены." : head;
}

/** Подсказка в «Моём»: разбор сцены к своему тексту не дописывается. */
export function customSceneHint(scene: SceneState, scenes: number): string | null {
  if (scene === null) return null;
  if (scenes > 1)
    return "Разбор сцены к своему тексту не дописывается. Сцен несколько, у каждой свой разбор, поэтому в заготовку он не входит: общий текст уйдёт во все пары.";
  return scene === "missing"
    ? "Разбор сцены к своему тексту не дописывается: его строки должны быть в самом тексте."
    : "Разбор сцены к своему тексту не дописывается: его строки должны быть в самом тексте. «Взять из авто» кладёт их в заготовку.";
}

/**
 * Строка «разбора нет» под промптом: в «Авто» сервис допишет его сам; в «Моём» не допишет, и при одной
 * сцене есть смысл разобрать сейчас и взять заготовку; при нескольких сценах — ни строки, ни кнопки.
 */
export function sceneMissingLine(custom: boolean, scenes: number): string | null {
  if (!custom) return "Разбора этой сцены ещё нет: строку кадра и замены предметов сервис допишет при запуске.";
  if (scenes === 1)
    return "Разбора этой сцены ещё нет, а к своему тексту он сам не допишется. Нажмите «Разобрать сейчас», затем «взять из авто».";
  return null;
}

/** В тексте есть блок разбора сцены. Это заголовки `generate.FRAMING_HEADER` и `generate.SCENE_CHANGES_HEADER`. */
export function hasSceneBlock(text: string): boolean {
  return text.includes("Framing of the scene image") || text.includes("Scene changes to make:");
}

export const FOREIGN_SCENE =
  "В тексте есть строка кадра и замены другой сцены, они уйдут в модель. Нажмите «взять из авто» или уберите их.";

/**
 * Разбор в тексте «Моего» — не этой сцены: сцен не одна, или заготовку брали у другого референса.
 * `takenRef === null` при одной сцене — неизвестно, откуда текст: не предупреждаем.
 */
export function foreignScene(text: string, takenRef: number | null, firstRef: number | null, scenes: number): boolean {
  return hasSceneBlock(text) && (scenes !== 1 || (takenRef !== null && firstRef !== takenRef));
}
